import { useLayoutEffect, useState, type CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import type {
  DatePickerPlacement,
  UseDatePickerPositionOptions,
  UseDatePickerPositionReturn,
} from './DatePickerModal.types';

const GAP = 8;
const MIN_HEIGHT = 160;

const FLIP: Record<DatePickerPlacement, DatePickerPlacement> = {
  'top-start': 'bottom-start',
  'top-end': 'bottom-end',
  'bottom-start': 'top-start',
  'bottom-end': 'top-end',
  'left-start': 'right-start',
  'left-end': 'right-end',
  'right-start': 'left-start',
  'right-end': 'left-end',
};

function getAvailableHeight(rect: DOMRect, p: DatePickerPlacement): number {
  switch (p) {
    case 'top-start':
      return rect.top - GAP;
    case 'top-end':
      return rect.top - GAP;
    case 'bottom-start':
      return window.innerHeight - rect.bottom - GAP;
    case 'bottom-end':
      return window.innerHeight - rect.bottom - GAP;
    case 'left-start':
      return window.innerHeight - rect.top - GAP;
    case 'left-end':
      return rect.bottom - GAP;
    case 'right-start':
      return window.innerHeight - rect.top - GAP;
    case 'right-end':
      return rect.bottom - GAP;
  }
}

function applyPlacementPosition(
  style: CSSProperties,
  rect: DOMRect,
  p: DatePickerPlacement
): void {
  switch (p) {
    case 'top-start':
      style.bottom = window.innerHeight - rect.top + GAP;
      style.left = rect.left;
      break;
    case 'top-end':
      style.bottom = window.innerHeight - rect.top + GAP;
      style.right = window.innerWidth - rect.right;
      break;
    case 'bottom-start':
      style.top = rect.bottom + GAP;
      style.left = rect.left;
      break;
    case 'bottom-end':
      style.top = rect.bottom + GAP;
      style.right = window.innerWidth - rect.right;
      break;
    case 'left-start':
      style.left = Math.max(GAP, rect.left - GAP - 320);
      style.top = rect.top;
      break;
    case 'left-end':
      style.left = Math.max(GAP, rect.left - GAP - 320);
      style.bottom = window.innerHeight - rect.bottom;
      break;
    case 'right-start':
      style.left = rect.right + GAP;
      style.top = rect.top;
      break;
    case 'right-end':
      style.left = rect.right + GAP;
      style.bottom = window.innerHeight - rect.bottom;
      break;
  }
}

export function useDatePickerPosition({
  isOpen,
  anchorEl,
  placement,
  compact,
  modalRef,
}: UseDatePickerPositionOptions): UseDatePickerPositionReturn {
  const [positionStyle, setPositionStyle] = useState<CSSProperties>({});
  const [contentStyle, setContentStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const compute = () => {
      if (!anchorEl) {
        // Centred dialog — transform on position:fixed is fine here (no anchor to track).
        setPositionStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        });
        setContentStyle({ minWidth: '320px', maxWidth: '320px' });
        return;
      }

      const rect = anchorEl.getBoundingClientRect();
      const naturalHeightDefault = compact ? 440 : 480;

      // Auto-flip: prefer the opposite side when the requested placement has
      // insufficient vertical space.
      const primaryAvailable = getAvailableHeight(rect, placement);
      const flipped = FLIP[placement];
      const flippedAvailable = getAvailableHeight(rect, flipped);
      const activePlacement =
        primaryAvailable < naturalHeightDefault &&
        flippedAvailable > primaryAvailable
          ? flipped
          : placement;

      const availableHeight = Math.max(
        MIN_HEIGHT,
        getAvailableHeight(rect, activePlacement)
      );

      // Detect browser zoom via devicePixelRatio.
      const dpr = window.devicePixelRatio ?? 1;
      const effectiveZoom =
        dpr % 2 === 0 || dpr === 1 ? 1 : dpr > 2 ? dpr / 2 : dpr;
      const finalZoomScale = effectiveZoom > 1.1 ? 1 / effectiveZoom : 1;

      // Measure actual content height to avoid "empty space" from forced naturalHeight.
      // If modalRef.current is available, we use its scrollHeight but make sure
      // we account for any scaling that happened previously if we were to use offsetHeight.
      // scrollHeight is generally the 'natural' height of the content.
      const contentHeightNeeded =
        modalRef.current?.scrollHeight ?? naturalHeightDefault;

      if (finalZoomScale < 1) {
        // ZOOM PATH — use transform:scale on the inner content div so that
        // position:fixed on the outer div is not affected by CSS zoom (which
        // can break left/right placements). The outer div is sized to the
        // visual (post-scale) dimensions so it clips and positions correctly.
        const visualWidth = 320 * finalZoomScale;
        const visualHeight = contentHeightNeeded * finalZoomScale;

        // Compute the visual top-left corner in viewport coords for every placement.
        let posTop: number;
        let posLeft: number;

        switch (activePlacement) {
          case 'bottom-start':
            posTop = rect.bottom + GAP;
            posLeft = rect.left;
            break;
          case 'bottom-end':
            posTop = rect.bottom + GAP;
            posLeft = rect.right - visualWidth;
            break;
          case 'top-start':
            posTop = rect.top - GAP - visualHeight;
            posLeft = rect.left;
            break;
          case 'top-end':
            posTop = rect.top - GAP - visualHeight;
            posLeft = rect.right - visualWidth;
            break;
          case 'left-start':
            posTop = rect.top;
            posLeft = rect.left - GAP - visualWidth;
            break;
          case 'left-end':
            posTop = rect.bottom - visualHeight;
            posLeft = rect.left - GAP - visualWidth;
            break;
          case 'right-start':
            posTop = rect.top;
            posLeft = rect.right + GAP;
            break;
          case 'right-end':
            posTop = rect.bottom - visualHeight;
            posLeft = rect.right + GAP;
            break;
        }

        // Clamp so the visual box stays fully inside the viewport.
        posTop = Math.max(
          GAP,
          Math.min(posTop, window.innerHeight - visualHeight - GAP)
        );
        posLeft = Math.max(
          GAP,
          Math.min(posLeft, window.innerWidth - visualWidth - GAP)
        );

        setPositionStyle({
          position: 'fixed',
          top: posTop,
          left: posLeft,
          width: `${Math.ceil(visualWidth)}px`,
          height: `${Math.ceil(visualHeight)}px`,
        });

        setContentStyle({
          width: '320px',
          height: 'auto', // Let content height be natural
          maxHeight: `${Math.floor((window.innerHeight - 2 * GAP) / finalZoomScale)}px`,
          transform: `scale(${finalZoomScale.toFixed(4)})`,
          transformOrigin: 'top left',
        });
      } else {
        // Normal zoom: constrain height to the available viewport space.
        const posStyle: CSSProperties = {
          position: 'fixed',
          maxWidth: '320px',
          minWidth: '320px',
        };

        if (
          activePlacement === 'bottom-start' ||
          activePlacement === 'bottom-end'
        ) {
          // The calendar flows downward from the anchor's bottom. When the
          // anchor sits high enough that the space below is shorter than the
          // natural content height, squashing height would clip the action
          // row via the inner `overflow-hidden`. Slide the picker upward
          // instead so it can show its full natural height, mirroring the
          // clamp the zoom path applies.
          const maxH = Math.min(
            contentHeightNeeded,
            window.innerHeight - 2 * GAP
          );
          const idealTop = rect.bottom + GAP;
          const posTop = Math.max(
            GAP,
            Math.min(idealTop, window.innerHeight - maxH - GAP)
          );

          posStyle.top = posTop;
          if (activePlacement === 'bottom-start') {
            posStyle.left = rect.left;
          } else {
            posStyle.right = window.innerWidth - rect.right;
          }
          posStyle.height = `${Math.max(MIN_HEIGHT, maxH)}px`;
          posStyle.maxHeight = `${Math.floor(window.innerHeight - 2 * GAP)}px`;
        } else if (
          activePlacement === 'left-start' ||
          activePlacement === 'right-start'
        ) {
          // For left-start / right-start the calendar's top edge is anchored
          // at rect.top and the calendar extends downward.  When the anchor
          // is low in a zoomed (smaller) viewport, there may not be enough
          // space below, which would constrain the calendar to a tiny
          // height.  Shift the calendar upward so it can always show the
          // full natural height, mirroring the clamp the zoom-path already
          // applies.
          applyPlacementPosition(posStyle, rect, activePlacement);
          const maxH = Math.min(
            contentHeightNeeded,
            window.innerHeight - 2 * GAP
          );
          const adjustedTop = Math.max(
            GAP,
            Math.min(rect.top, window.innerHeight - maxH - GAP)
          );
          const adjustedAvailable = Math.max(
            MIN_HEIGHT,
            window.innerHeight - adjustedTop - GAP
          );
          const adjustedHeight = Math.floor(
            Math.min(contentHeightNeeded, adjustedAvailable)
          );
          posStyle.top = adjustedTop;
          posStyle.height = `${Math.max(MIN_HEIGHT, adjustedHeight)}px`;
          posStyle.maxHeight = `${Math.floor(adjustedAvailable)}px`;
        } else {
          applyPlacementPosition(posStyle, rect, activePlacement);
          // Never allow calculatedHeight to exceed availableHeight
          const calculatedHeight = Math.floor(
            Math.min(contentHeightNeeded, availableHeight)
          );
          posStyle.height = `${Math.max(MIN_HEIGHT, calculatedHeight)}px`;
          posStyle.maxHeight = `${Math.floor(availableHeight)}px`;
        }

        setPositionStyle(posStyle);
        setContentStyle({ width: '100%', height: '100%' });
      }
    };

    compute();

    // Suppress ResizeObserver during scroll ticks to prevent a cascade:
    // scroll → height changes → ResizeObserver fires → second compute → 2× lag.
    // The flag stays true until the rAF fires (after ResizeObserver callbacks,
    // before the next paint), then resets so genuine content resizes still work.
    let isScrolling = false;

    const scrollHandler = () => {
      isScrolling = true;
      requestAnimationFrame(() => {
        isScrolling = false;
      });
      flushSync(compute);
    };

    // Use ResizeObserver to detect content changes (switching months/years)
    // and recompute position/height immediately.
    let observer: ResizeObserver | null = null;
    if (modalRef.current) {
      observer = new ResizeObserver(() => {
        if (!isScrolling) {
          compute();
        }
      });
      observer.observe(modalRef.current);
    }

    window.addEventListener('resize', compute);
    window.addEventListener('scroll', scrollHandler, true);
    window.visualViewport?.addEventListener('resize', compute);
    window.visualViewport?.addEventListener('scroll', scrollHandler);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', scrollHandler, true);
      window.visualViewport?.removeEventListener('resize', compute);
      window.visualViewport?.removeEventListener('scroll', scrollHandler);
    };
  }, [isOpen, anchorEl, placement, compact, modalRef]);

  return { positionStyle, contentStyle };
}
